import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'

declare const microsoftTeams: any

type ConsentStatus = 'LOADING' | 'READY' | 'SUBMITTING' | 'ERROR'
type ExplicitConsentResponse = 'CONSENTED' | 'DECLINED'

@Component({
  selector: 'app-teams-consent',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './teams-consent.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './teams-consent.component.scss',
})
export class TeamsConsentComponent implements OnInit, OnDestroy {
  status: ConsentStatus = 'LOADING'
  errorMessage: string | null = null
  selectedOption: boolean | null = null

  private readonly apiBaseUrl = '/api/bot'
  private hasSubmitted = false
  private ssoToken: string | null = null

  constructor(
    private readonly ngZone: NgZone,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly translate: TranslateService
  ) {}

  ngOnInit() {
    this.loadTeamsSdk()
    window.addEventListener('pagehide', this.handlePageHide)
  }

  ngOnDestroy() {
    window.removeEventListener('pagehide', this.handlePageHide)
  }

  confirm() {
    if (this.selectedOption === null) {
      return
    }
    this.submitResponse(this.selectedOption ? 'CONSENTED' : 'DECLINED')
  }

  // Bound as a field, not a prototype method, so the same reference passed to addEventListener
  // can be passed to removeEventListener.
  private readonly handlePageHide = () => {
    if (this.hasSubmitted || this.ssoToken === null) {
      return
    }
    const callId = new URLSearchParams(window.location.search).get('callId')
    if (callId === null) {
      return
    }
    fetch(`${this.apiBaseUrl}/${callId}/consent-dismissed`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${this.ssoToken}` },
    })
  }

  private submitResponse(response: ExplicitConsentResponse) {
    this.hasSubmitted = true
    this.status = 'SUBMITTING'

    const callId = new URLSearchParams(window.location.search).get('callId')

    try {
      microsoftTeams.tasks.submitTask({ consentResponse: response, callId })
    } catch {
      this.hasSubmitted = false
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_SUBMIT')
        this.changeDetector.detectChanges()
      })
    }
  }

  private loadTeamsSdk() {
    if (typeof microsoftTeams !== 'undefined') {
      this.initializeTeams()
      return
    }
    const script = document.createElement('script')
    script.src = 'https://res.cdn.office.net/teams-js/2.24.0/js/MicrosoftTeams.min.js'
    script.onload = () => {
      this.initializeTeams()
    }
    script.onerror = () => {
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_LOAD')
        this.changeDetector.detectChanges()
      })
    }
    document.head.appendChild(script)
  }

  private async fetchSsoToken(): Promise<string | null> {
    try {
      return await microsoftTeams.authentication.getAuthToken()
    } catch (error) {
      console.warn('Failed to get Teams SSO token', error)
      return null
    }
  }

  private async initializeTeams() {
    try {
      const initTimeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Teams SDK timeout')), 5000))
      await Promise.race([microsoftTeams.app.initialize(), initTimeout])

      this.ssoToken = await this.fetchSsoToken()

      this.ngZone.run(() => {
        this.status = 'READY'
        this.changeDetector.detectChanges()
      })
    } catch {
      this.ngZone.run(() => {
        this.status = 'ERROR'
        this.errorMessage = this.translate.instant('TEAMS_CONSENT.ERROR_INIT')
        this.changeDetector.detectChanges()
      })
    }
  }
}
